import { useCallback, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetNewReportQuery, type GetNewReportQuery } from '../../../generated/hooks';
import { buildAnswerGroupTree } from '../buildAnswerGroups';
import { createAnswerGroupContext } from '../answerGroupSummaries';
import { createAnswerGroupRowSelector } from '../answerGroupRows';
import { createComparisonGroupSelector, CURRENT_REPORT_GROUP_ID, WHOLE_REPORT_GROUP_ID } from '../comparisonGroups';
import { useDefinitionCalculations } from '../definition/ReportDefinitionContext';
import {
  applyFilters,
  calculateAdHocGroup,
  filterReportData,
  getAdHocGroupKey,
  type AdHocGroupConfig,
  type ReportFilters,
} from '../filtering';
import type { AnswerGroup, AnswerGroupRowMode, QuestionScoringMap } from '../types';

const EMPTY_FILTERS: ReportFilters = { teamIds: new Set(), answerSelections: [] };
const EMPTY_SCORING: QuestionScoringMap = {};
const EMPTY_GROUPS: AnswerGroup[] = [];
const EMPTY_GROUP_CONFIGS = new Map<string, AdHocGroupConfig>();

/** Session-local state (drafts, filters, ad-hoc groups) belongs to one access link; a new link starts clean. */
export const reportScope = (accessToken: string, publicAccess: boolean) => JSON.stringify([accessToken, publicAccess]);

/** Loads the report and keeps the last result of the same link while a language change refetches. */
export function useNewReportData(accessToken: string, languageCode: string, publicAccess: boolean) {
  const {
    data: queryData,
    loading,
    error,
  } = useGetNewReportQuery({ variables: { accessToken, languageCode, publicAccess } });
  const lastData = useRef<{ token: string; data: typeof queryData }>();
  if (queryData) lastData.current = { token: accessToken, data: queryData };
  const data = queryData ?? (lastData.current?.token === accessToken ? lastData.current.data : undefined);
  return { loading, error, data };
}

/**
 * Builds the calculated report. Metric calculations come from `ReportDefinitionProvider`, so this re-runs only
 * when a calculation changes, never for title, layout or presentation edits of the draft.
 */
export function useNewReport(
  accessToken: string,
  languageCode: string,
  publicAccess: boolean,
  data: GetNewReportQuery | undefined,
) {
  const customMetrics = useDefinitionCalculations();
  const { t } = useTranslation('newReport');
  const { t: tReport } = useTranslation('Report');
  const scope = reportScope(accessToken, publicAccess);
  const [filterState, setFilterState] = useState({ scope, value: EMPTY_FILTERS });
  const [scoringState, setScoringState] = useState({ scope, value: EMPTY_SCORING });
  const [adHocState, setAdHocState] = useState({ scope, configs: new Map<string, AdHocGroupConfig>() });
  const filters = filterState.scope === scope ? filterState.value : EMPTY_FILTERS;
  const questionScoring = scoringState.scope === scope ? scoringState.value : EMPTY_SCORING;

  const snapshot = useMemo(() => {
    if (!data) return null;
    const context = createAnswerGroupContext(
      data.getNewReport,
      questionScoring,
      {
        anonymous: (count) => t('table.anonymousGroup', { count }),
        unassigned: t('table.unassigned'),
        withoutSupervisor: tReport('withoutSupervisor'),
        byAnswer: t('table.byAnswer'),
      },
      customMetrics,
    );
    // This cache belongs to this exact data/language/scoring snapshot, never to the next query result.
    const cache = new Map<string, AnswerGroup>();
    const getAdHocGroup = (config: AdHocGroupConfig): AnswerGroup | null => {
      const key = getAdHocGroupKey(config);
      const group = cache.get(key) ?? calculateAdHocGroup({ ...config, label: undefined }, context);
      if (!group) return null;
      cache.set(key, group);
      return config.label ? { ...group, label: config.label } : group;
    };
    const tree = buildAnswerGroupTree(context);
    return {
      scope,
      languageCode,
      context,
      tree,
      getComparisonGroups: createComparisonGroupSelector(context, tree, t('tableTest.currentReport')),
      getWholeReportGroup: createComparisonGroupSelector(
        context,
        tree,
        t('tableTest.wholeReport'),
        WHOLE_REPORT_GROUP_ID,
      ),
      getAdHocGroup,
      selectRows: createAnswerGroupRowSelector(context),
    };
  }, [data, questionScoring, customMetrics, t, tReport, languageCode, scope]);

  // The unfiltered baseline never depends on the current filters; it only needs to be computed once per snapshot.
  const wholeReportGroup = useMemo(
    () => (snapshot ? snapshot.getWholeReportGroup(snapshot.tree).get(WHOLE_REPORT_GROUP_ID) ?? null : null),
    [snapshot],
  );

  const filteredData = useMemo(
    () => (snapshot ? applyFilters(snapshot.tree, filters, snapshot.context, snapshot.getAdHocGroup) : null),
    [snapshot, filters],
  );
  const currentGroup = useMemo(
    () =>
      snapshot && filteredData ? snapshot.getComparisonGroups(filteredData).get(CURRENT_REPORT_GROUP_ID) ?? null : null,
    [snapshot, filteredData],
  );
  const filteredRawData = useMemo(
    () => (snapshot ? filterReportData(snapshot.context, filters) : null),
    [snapshot, filters],
  );
  const adHocGroups = useMemo(() => {
    const groups = new Map<string, AnswerGroup>();
    if (snapshot && adHocState.scope === scope) {
      adHocState.configs.forEach((config, key) => {
        const group = snapshot.getAdHocGroup(config);
        if (group) groups.set(key, group);
      });
    }
    return groups;
  }, [snapshot, adHocState, scope]);

  const setFilters = useCallback<Dispatch<SetStateAction<ReportFilters>>>(
    (update) => {
      setFilterState((previous) => {
        const current = previous.scope === scope ? previous.value : EMPTY_FILTERS;
        return { scope, value: typeof update === 'function' ? update(current) : update };
      });
    },
    [scope],
  );
  const setQuestionScoring = useCallback<Dispatch<SetStateAction<QuestionScoringMap>>>(
    (update) => {
      setScoringState((previous) => {
        const current = previous.scope === scope ? previous.value : EMPTY_SCORING;
        return { scope, value: typeof update === 'function' ? update(current) : update };
      });
    },
    [scope],
  );
  const getAnswerGroup = useCallback((config: AdHocGroupConfig) => snapshot?.getAdHocGroup(config) ?? null, [snapshot]);
  const addAnswerGroup = useCallback(
    (config: AdHocGroupConfig): AnswerGroup | null => {
      const group = snapshot?.getAdHocGroup(config) ?? null;
      if (!group) return null;
      // Copy caller-owned filter collections; state updaters have no nested state updates or calculations.
      const storedConfig = {
        ...config,
        teamIds: new Set(config.teamIds),
        answerSelections: config.answerSelections.map((selection) => ({ ...selection })),
      };
      setAdHocState((previous) => ({
        scope,
        configs: new Map(previous.scope === scope ? previous.configs : []).set(getAdHocGroupKey(config), storedConfig),
      }));
      return group;
    },
    [snapshot, scope],
  );
  const removeAnswerGroup = useCallback(
    (key: string) => {
      setAdHocState((previous) => {
        const configs = new Map(previous.scope === scope ? previous.configs : []);
        configs.delete(key);
        return { scope, configs };
      });
    },
    [scope],
  );
  const selectComparisonGroups = useCallback(
    (ids: ReadonlySet<string>): AnswerGroup[] => {
      if (!snapshot || !filteredData || !ids.size) return EMPTY_GROUPS;
      const saved = new Map([...adHocGroups.values()].map((group) => [group.id, group]));
      return [...ids].flatMap((id) => {
        const group = saved.get(id) ?? snapshot.getComparisonGroups(filteredData).get(id);
        return group ? [group] : [];
      });
    },
    [snapshot, filteredData, adHocGroups],
  );
  const selectRows = useCallback(
    (groups: AnswerGroup[], mode: AnswerGroupRowMode) => snapshot?.selectRows(groups, mode) ?? EMPTY_GROUPS,
    [snapshot],
  );

  const adHocGroupConfigs = adHocState.scope === scope ? adHocState.configs : EMPTY_GROUP_CONFIGS;
  const resolvedQuestionScoring = snapshot?.context.scoring ?? EMPTY_SCORING;

  return useMemo(
    () => ({
      accessToken,
      languageCode,
      rawData: data?.getNewReport ?? null,
      filteredRawData,
      answerGroupTree: snapshot?.tree ?? null,
      filteredData,
      currentGroup,
      wholeReportGroup,
      filters,
      setFilters,
      addAnswerGroup,
      getAnswerGroup,
      removeAnswerGroup,
      adHocGroups,
      adHocGroupConfigs,
      selectComparisonGroups,
      questionScoring: resolvedQuestionScoring,
      setQuestionScoring,
      selectRows,
    }),
    [
      accessToken,
      languageCode,
      data,
      filteredRawData,
      snapshot,
      filteredData,
      currentGroup,
      wholeReportGroup,
      filters,
      setFilters,
      addAnswerGroup,
      getAnswerGroup,
      removeAnswerGroup,
      adHocGroups,
      adHocGroupConfigs,
      selectComparisonGroups,
      resolvedQuestionScoring,
      setQuestionScoring,
      selectRows,
    ],
  );
}

export type NewReport = ReturnType<typeof useNewReport>;
